import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import JsonLd from '@/components/feature/JsonLd';
import GossipToolbar from '@/pages/gossip/components/GossipToolbar';
import { articles, categories } from '@/mocks/gossip';

const PAGE_SIZE = 6;

export default function GossipList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(searchParams.get('q') || '');
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [sort, setSort] = useState<'latest' | 'hot'>('latest');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setKeyword(searchParams.get('q') || '');
    setCategory(searchParams.get('category') || 'all');
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
  }, [keyword, category, sort]);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const list = articles.filter((article) => {
      const matchCategory = category === 'all' || article.categorySlug === category;
      const matchKeyword =
        !kw ||
        article.title.toLowerCase().includes(kw) ||
        article.summary.toLowerCase().includes(kw) ||
        article.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchKeyword;
    });
    return list.sort((a, b) => (sort === 'hot' ? b.views - a.views : a.date < b.date ? 1 : -1));
  }, [keyword, category, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const activeName = categories.find((c) => c.slug === category)?.name;

  const handleCategory = (slug: string) => {
    setCategory(slug);
    const next = new URLSearchParams(searchParams);
    if (slug === 'all') {
      next.delete('category');
    } else {
      next.set('category', slug);
    }
    setSearchParams(next, { replace: true });
  };

  const handleKeyword = (value: string) => {
    setKeyword(value);
    const next = new URLSearchParams(searchParams);
    if (value.trim()) {
      next.set('q', value.trim());
    } else {
      next.delete('q');
    }
    setSearchParams(next, { replace: true });
  };

  const pageTitle = activeName ? `${activeName} - 吃瓜广场 | 爆瓜福利` : '吃瓜广场 - 娱乐爆料文章列表 | 爆瓜福利';

  return (
    <div className="page-enter min-h-screen bg-background-50">
      <title>{pageTitle}</title>
      <meta
        name="description"
        content="爆瓜福利吃瓜广场，按明星动态、影视热议、综艺八卦、音乐现场、网红社区与情感话题分类浏览最新爆料文章，支持关键词搜索与热度排序。"
      />
      <meta name="keywords" content="爆瓜福利,吃瓜广场,娱乐爆料文章,明星动态,影视热议,综艺八卦" />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: '吃瓜广场',
          description: '爆瓜福利吃瓜广场，聚合最新娱乐爆料与话题文章。',
          inLanguage: 'zh-CN',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
              { '@type': 'ListItem', position: 2, name: '吃瓜广场', item: '/gossip' },
            ],
          },
        }}
      />

      <SiteHeader />
      <main className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: activeName ? `吃瓜广场 · ${activeName}` : '吃瓜广场' }]} />

        <header className="mt-6 border-b-2 border-foreground-950 pb-6">
          <h1 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            {activeName ? `${activeName}专栏` : '吃瓜广场'}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600">
            这里汇集了爆瓜福利收录的全部爆料与话题文章，按分类筛选或输入关键词，快速定位你感兴趣的那一条。
          </p>
        </header>

        <div className="mt-8">
          <GossipToolbar
            activeCategory={category}
            keyword={keyword}
            sort={sort}
            onCategory={handleCategory}
            onKeyword={handleKeyword}
            onSort={setSort}
          />
        </div>

        <div className="mt-6 flex items-center justify-between text-sm text-foreground-500">
          <span>
            共 <strong className="text-foreground-900">{filtered.length}</strong> 篇文章
          </span>
          <span>第 {page} / {totalPages} 页</span>
        </div>

        {paged.length > 0 ? (
          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {paged.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
            <i className="ri-emotion-unhappy-line text-4xl text-foreground-400"></i>
            <p className="mt-4 font-heading text-base font-bold text-foreground-900">没有找到相关内容</p>
            <p className="mt-2 text-sm text-foreground-500">换个关键词或选择其他分类试试看</p>
            <button
              type="button"
              onClick={() => {
                setKeyword('');
                setCategory('all');
                setSearchParams({}, { replace: true });
              }}
              className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选
            </button>
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-2">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="flex h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="ri-arrow-left-s-line"></i>
              上一页
            </button>
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setPage(index + 1)}
                className={`flex h-9 w-9 items-center justify-center rounded-md text-sm transition-colors ${
                  page === index + 1
                    ? 'bg-primary-500 text-background-50'
                    : 'border border-background-300 text-foreground-700 hover:border-primary-300'
                }`}
              >
                {index + 1}
              </button>
            ))}
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="flex h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-40"
            >
              下一页
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}